import React, {useEffect, useMemo, useState} from "react";
import "./DesignerPageHeaderControls.css";

const DesignerPageHeaderControls = ({
    pages, selectedPageId, setSelectedPageId, selectedPage,
    newPageTitle, setNewPageTitle, creatingPage, onCreatePage,
    onDuplicatePage, onDeletePage, saving, hasUnsavedChanges,
    onSaveChanges, onResetChanges, onUpdatePageSettings,
    onReorderPages, reorderingPages,
}) => {
    const [collapsed, setCollapsed] = useState(false);
    const [editing, setEditing] = useState(false);
    const [copyFeedback, setCopyFeedback] = useState(false);
    const [draft, setDraft] = useState(null);
    const [draggingPageId, setDraggingPageId] = useState(null);
    const [dragOverPageId, setDragOverPageId] = useState(null);

    useEffect(() => {
        if (!selectedPage) {
            setDraft(null);
            setEditing(false);
            return;
        }
        setDraft({
            title: selectedPage.title || "",
            description: selectedPage.description || "",
            width: selectedPage.width || 1600,
            height: selectedPage.height || 900,
            background_color: selectedPage.background_color || "#ffffff",
            background_image: selectedPage.background_image || "",
            show_grid: selectedPage.show_grid !== false,
            is_active: selectedPage.is_active !== false,
            is_published: !!selectedPage.is_published,
            grid_size: selectedPage.grid_size || 20,
        });
        setEditing(false);
        setCopyFeedback(false);
    }, [selectedPage]);

    const createdLabel = useMemo(() => selectedPage?.created_at ? new Date(selectedPage.created_at).toLocaleDateString() : "", [selectedPage]);
    const updateDraft = (patch) => setDraft((current) => ({...current, ...patch}));

    const savePageDetails = async () => {
        if (!draft?.title.trim()) return;
        await onUpdatePageSettings({
            ...draft,
            title: draft.title.trim(),
            description: draft.description.trim(),
            width: Math.max(100, Number(draft.width) || 1600),
            height: Math.max(100, Number(draft.height) || 900),
            grid_size: Math.max(5, Number(draft.grid_size) || 20),
        });
        setEditing(false);
    };

    const copyRuntimeLink = async () => {
        if (!selectedPageId) return;
        await navigator.clipboard.writeText(`${window.location.origin}/runtime/${selectedPageId}`);
        setCopyFeedback(true);
        window.setTimeout(() => setCopyFeedback(false), 1800);
    };

    const startPageDrag = (event, pageId) => {
        if (reorderingPages) return;
        setDraggingPageId(String(pageId));
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(pageId));
    };

    const finishPageDrag = () => {
        setDraggingPageId(null);
        setDragOverPageId(null);
    };

    const dropPage = (event, targetPageId) => {
        event.preventDefault();
        const sourceId = draggingPageId || event.dataTransfer.getData("text/plain");
        const targetId = String(targetPageId);
        finishPageDrag();
        if (!sourceId || sourceId === targetId) return;

        const orderedPages = [...(pages || [])];
        const sourceIndex = orderedPages.findIndex((page) => String(page.id) === String(sourceId));
        const targetIndex = orderedPages.findIndex((page) => String(page.id) === targetId);
        if (sourceIndex < 0 || targetIndex < 0) return;

        const [movedPage] = orderedPages.splice(sourceIndex, 1);
        orderedPages.splice(targetIndex, 0, movedPage);
        onReorderPages?.(orderedPages);
    };

    if (collapsed) return <button type="button" className="dph-collapsed-btn" onClick={() => setCollapsed(false)}>Page Controls</button>;

    return <div className="dph-panel">
        <div className="dph-header">
            <div><span className="dph-kicker">Designer</span><h3 className="dph-title">Page Controls</h3></div>
            <button type="button" className="dph-collapse-btn" onClick={() => setCollapsed(true)} aria-label="Collapse page controls">−</button>
        </div>

        <div className="dph-create-row">
            <input className="dph-input" type="text" value={newPageTitle} onChange={(e) => setNewPageTitle(e.target.value)} placeholder="New page title" onKeyDown={(e) => { if (e.key === "Enter") onCreatePage(); }} />
            <button type="button" className="dph-btn dph-btn--green" disabled={creatingPage || !newPageTitle.trim()} onClick={onCreatePage}>{creatingPage ? "Adding…" : "Add"}</button>
        </div>

        <div className="dph-page-list" aria-label="Designer pages">
            {(pages || []).map((page) => {
                const pageId = String(page.id);
                const isSelected = pageId === String(selectedPageId);
                const isDragging = pageId === draggingPageId;
                const isDragTarget = pageId === dragOverPageId && !isDragging;
                return <div
                    key={page.id}
                    className={`dph-page-item ${isSelected ? "is-selected" : ""} ${isDragging ? "is-dragging" : ""} ${isDragTarget ? "is-drag-target" : ""}`}
                    draggable={!reorderingPages}
                    onDragStart={(event) => startPageDrag(event, page.id)}
                    onDragEnter={() => setDragOverPageId(pageId)}
                    onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(event) => dropPage(event, page.id)}
                    onDragEnd={finishPageDrag}
                >
                    <span className="dph-drag-handle" aria-hidden="true" title="Drag to reorder">⋮⋮</span>
                    <button type="button" className="dph-page-select" onClick={() => setSelectedPageId(pageId)}>
                        <span className="dph-page-name">{page.title}</span>
                        <span className={`dph-status-dot ${page.is_published ? "is-live" : ""}`} title={page.is_published ? "Published" : "Draft"} />
                    </button>
                </div>;
            })}
            {(!pages || pages.length === 0) && <div className="dph-empty">No pages yet.</div>}
        </div>

        {selectedPage && draft && <div className="dph-selected-card">
            <div className="dph-selected-heading">
                <div><strong>{selectedPage.title}</strong><small>{selectedPage.is_published ? "Published" : "Draft"}{createdLabel ? ` · Created ${createdLabel}` : ""}</small></div>
                <button type="button" className="dph-edit-toggle" onClick={() => setEditing((value) => !value)}>{editing ? "Close" : "Edit"}</button>
            </div>

            {editing && <div className="dph-editor">
                <label className="dph-field"><span>Name</span><input className="dph-input" value={draft.title} onChange={(e) => updateDraft({title: e.target.value})} /></label>
                <label className="dph-field"><span>Description</span><textarea className="dph-input dph-textarea" value={draft.description} onChange={(e) => updateDraft({description: e.target.value})} /></label>
                <div className="dph-grid-2">
                    <label className="dph-field"><span>Width</span><input className="dph-input" type="number" value={draft.width} onChange={(e) => updateDraft({width: e.target.value})} /></label>
                    <label className="dph-field"><span>Height</span><input className="dph-input" type="number" value={draft.height} onChange={(e) => updateDraft({height: e.target.value})} /></label>
                </div>
                <label className="dph-field"><span>Background</span><div className="dph-color-row"><input type="color" value={draft.background_color} onChange={(e) => updateDraft({background_color: e.target.value})} /><input className="dph-input" value={draft.background_color} onChange={(e) => updateDraft({background_color: e.target.value})} /></div></label>
                <label className="dph-field"><span>Background image</span><input className="dph-input" value={draft.background_image} onChange={(e) => updateDraft({background_image: e.target.value})} placeholder="Optional image URL" /></label>
                <label className="dph-field"><span>Grid size</span><input className="dph-input" type="number" min="5" value={draft.grid_size} onChange={(e) => updateDraft({grid_size: e.target.value})} /></label>
                <div className="dph-check-grid">
                    <label className="dph-check"><input type="checkbox" checked={draft.show_grid} onChange={(e) => updateDraft({show_grid: e.target.checked})} />Show grid</label>
                    <label className="dph-check"><input type="checkbox" checked={draft.is_active} onChange={(e) => updateDraft({is_active: e.target.checked})} />Active</label>
                    <label className="dph-check"><input type="checkbox" checked={draft.is_published} onChange={(e) => updateDraft({is_published: e.target.checked})} />Published</label>
                </div>
                <button type="button" className="dph-btn dph-btn--blue dph-btn--full" disabled={!draft.title.trim()} onClick={savePageDetails}>Save Page Settings</button>
            </div>}

            <div className="dph-page-actions">
                <button type="button" className="dph-btn dph-btn--white" onClick={copyRuntimeLink}>{copyFeedback ? "Copied" : "Copy Link"}</button>
                <button type="button" className="dph-btn dph-btn--white" onClick={onDuplicatePage}>Duplicate</button>
                <button type="button" className="dph-btn dph-btn--danger" onClick={onDeletePage}>Delete</button>
            </div>
        </div>}

        <div className="dph-save-actions">
            <button type="button" className="dph-btn dph-btn--blue" disabled={saving || !hasUnsavedChanges} onClick={onSaveChanges}>{saving ? "Saving…" : "Save Canvas"}</button>
            <button type="button" className="dph-btn dph-btn--white" disabled={!hasUnsavedChanges} onClick={onResetChanges}>Reset</button>
        </div>
    </div>;
};

export default DesignerPageHeaderControls;
